import React, { useState } from 'react';
import { Alert, Button, Tooltip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import AlertDialog from 'components/AlertDialog';
import { Disclosure, useDisclosure } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import QuestionMarkIcon from '@mui/icons-material/HelpOutlineOutlined';
import AppDatePicker from 'components/FormElements/AppDatePicker';
import { InfoOutlined } from '@mui/icons-material';
import { type as feeConst } from 'adminConstants/typeConstant';
import useStyles from './styles';
import { useNotification } from 'hooks/ui/useNotification';
import { format } from 'date-fns';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api, { type ApiResponses } from 'lib/trpcClient';

type Props = {
  investmentId: string;
  freezeDisclosure: Disclosure;
  hasFrozenFundsMoved: boolean;
  frozenOn?: string;
  availableFunds: number;
  fees: Array<{
    date: string;
    isFrozen?: boolean;
    custodian?: boolean;
    viaExitId?: unknown;
    feeAccounting?: unknown;
  }> | null;
  recentShareholdingDate: Date | null;
};

const ReversedButton = withStyles(Button, (theme: any) => ({
  root: {
    color: theme.palette.text.white,
    borderColor: theme.palette.borderColor.white,
    fontWeight: 700,
    '&:hover': {
      borderColor: theme.palette.borderColor.white,
    },
  },
}));

const InvestmentsFreeze: React.FC<Props> = ({
  frozenOn,
  investmentId,
  freezeDisclosure,
  fees,
  hasFrozenFundsMoved,
  availableFunds,
  recentShareholdingDate,
}) => {
  const { classes } = useStyles();
  const unfreezeDisclosure = useDisclosure();
  const moveFundsDisclosure = useDisclosure();
  const { error } = useNotification();
  const [freezeStartDate, setFreezeStartDate] = useState<Date | null>(
    new Date(),
  );

  const recentCustodianExportedFeeDate = fees
    ?.filter((fee) => fee.custodian)
    .map((fee) => new Date(fee.date))
    .sort((date1: Date, date2: Date) => date2.getTime() - date1.getTime())[0];

  const earliestPossibleFreezeTime = Math.max(
    ...[
      recentCustodianExportedFeeDate?.getTime() ?? 0,
      recentShareholdingDate?.getTime() ?? 0,
    ],
  );
  const earliestPossibleFreezeDate = earliestPossibleFreezeTime
    ? new Date(earliestPossibleFreezeTime)
    : undefined;

  const canBeUnfrozen = !fees?.find(
    (fee) =>
      fee.isFrozen &&
      ((fee.feeAccounting === feeConst.feeAccounting.chargedToInvestor &&
        fee.viaExitId) ||
        fee.custodian),
  );

  const isFrozen = frozenOn && new Date() >= new Date(frozenOn);

  const {
    error: freezeError,
    mutate: freezeInvestment,
    isPending: isFreezing,
  } = api.investment.freeze.useMutation(
    useHandleTrpcMutation<ApiResponses['investment']['freeze']>()({
      successMessage: 'Investment freeze status updated successfully.',
      onSuccess: (data) => {
        if (!data.success) {
          error('Freezing subscription failed.');
          return;
        }
        freezeDisclosure.onClose();
      },
    }),
  );

  const {
    error: unfreezeError,
    mutate: unfreezeInvestment,
    isPending: isUnfreezing,
  } = api.investment.freeze.useMutation(
    useHandleTrpcMutation<ApiResponses['investment']['freeze']>()({
      successMessage: 'Investment freeze status updated successfully.',
      onSuccess: (response) => {
        if (!response.success) {
          error('Freezing subscription failed.');
          return;
        }
        unfreezeDisclosure.onClose();
      },
    }),
  );

  const {
    error: moveFrozenFundsError,
    mutate: moveFrozenFunds,
    isPending: isMovingFrozenFunds,
  } = api.investment.moveFunds.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Frozen funds moved successfully.',
      onSuccess: () => {
        moveFundsDisclosure.onClose();
      },
    }),
  );

  const handleFreeze = () => {
    if (isFreezing || isFrozen) return;

    freezeInvestment({
      freezeStartDate: freezeStartDate?.toISOString() ?? null,
      investmentId,
    });
  };

  const handleUnfreeze = () => {
    if (isUnfreezing || !canBeUnfrozen || !isFrozen || hasFrozenFundsMoved)
      return;

    unfreezeInvestment({ freezeStartDate: null, investmentId });
  };

  const handleMoveFrozenFunds = () => {
    if (!isFrozen || hasFrozenFundsMoved) return;

    moveFrozenFunds({ investmentId });
  };

  return (
    <>
      {isFrozen ? (
        <div className={classes.freezeLabel}>
          <span>
            This subscription was frozen on{' '}
            {frozenOn ? format(new Date(frozenOn), 'dd/MM/yyyy') : ''}
          </span>
          <ReversedButton
            size="small"
            variant="outlined"
            color="primary"
            onClick={unfreezeDisclosure.onOpen}
            disabled={!canBeUnfrozen || hasFrozenFundsMoved}
          >
            Unfreeze subscription
          </ReversedButton>
          {!canBeUnfrozen ? (
            <Tooltip
              title="This exit is unable to be unfrozen due to one or multiple exits or custodian exports occurring after the freeze date."
              placement="top"
            >
              <InfoOutlined />
            </Tooltip>
          ) : null}
          <ReversedButton
            size="small"
            variant="outlined"
            color="primary"
            onClick={moveFundsDisclosure.onOpen}
            disabled={hasFrozenFundsMoved}
          >
            Move frozen funds
          </ReversedButton>
        </div>
      ) : null}
      <AlertDialog
        icon={QuestionMarkIcon}
        open={freezeDisclosure.isOpen}
        onClose={freezeDisclosure.onClose}
        onConfirm={handleFreeze}
        title="Are you sure?"
        content={
          <div className={classes.dialogContent}>
            <p className={classes.dialogContentText}>
              If frozen, fees will accrue pro rata to deployed capital and this
              subscription will be excluded from future allocations.
            </p>
            <p className={classes.dialogContentText}>
              Any allocations you add before the freeze date could make freeze
              fee calculations incorrect. If this occurs, please unfreeze and
              freeze the investor again
            </p>
            <div className={classes.freezeDate}>
              <label htmlFor="freezeStartDate">Freeze start date:</label>
              <AppDatePicker
                id="freezeStartDate"
                name="freezeStartDate"
                value={freezeStartDate}
                minDate={earliestPossibleFreezeDate}
                onChange={(date) => {
                  setFreezeStartDate(date);
                }}
                disableFuture={true}
              />
            </div>
            {freezeError ? (
              <Alert severity="error" className="mt-5">
                Error when trying to freeze subscription. Please try again.
              </Alert>
            ) : null}
          </div>
        }
        confirmBtnProps={{ disabled: isFreezing }}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Freeze subscription',
        }}
      />
      <AlertDialog
        icon={QuestionMarkIcon}
        open={unfreezeDisclosure.isOpen}
        onClose={unfreezeDisclosure.onClose}
        onConfirm={handleUnfreeze}
        title="Are you sure?"
        content={
          <div className={classes.dialogContent}>
            <p className={classes.moveFundsDialogContentText}>
              When unfrozen, all freeze-related fee reductions will be undone.
              If this subscription missed deal allocations while frozen, it may
              not be possible to fully deploy the subscription's capital.
            </p>
            {unfreezeError ? (
              <Alert severity="error" className="mt-5">
                Error when trying to unfreeze subscription. Please try again.
              </Alert>
            ) : null}
          </div>
        }
        confirmBtnProps={{ disabled: isFreezing }}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Unfreeze subscription',
        }}
      />
      <AlertDialog
        open={moveFundsDisclosure.isOpen}
        onClose={moveFundsDisclosure.onClose}
        onConfirm={handleMoveFrozenFunds}
        title="Are you sure?"
        content={
          <div className={classes.dialogContent}>
            <p className={classes.moveFundsDialogContentText}>
              Doing this will clear {numberToCurrencyString(availableFunds)}{' '}
              from the subscription and transfer it to the investor's cash
              balance. This cannot be undone.
            </p>
            {moveFrozenFundsError ? (
              <Alert severity="error" className="mt-5">
                Error when trying to move frozen funds. Please try again.
              </Alert>
            ) : null}
          </div>
        }
        confirmBtnProps={{ disabled: isMovingFrozenFunds }}
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Transfer funds',
        }}
      />
    </>
  );
};

export default InvestmentsFreeze;
